import React, { useCallback, useEffect, useState } from 'react';
import { FolderDown, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { bookingsApi, cargoApi } from '../../services/api';
import { attachmentFilename } from '../../lib/utils';
import { LaneHeader } from './LaneHeader';
import { ProfileForm } from './ProfileForm';
import { RequirementList } from './RequirementList';
import { ReadinessPanel } from './ReadinessPanel';
import { ExternalDocDialog } from './ExternalDocDialog';
import { DocumentPreview } from '../shared/DocumentPreview';
import { toast } from 'sonner';

function saveBlob(res, fallback, type) {
  const url = window.URL.createObjectURL(new Blob([res.data], { type: type }));
  const a = document.createElement('a');
  a.href = url; a.setAttribute('download', attachmentFilename(res, fallback));
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
}

async function errorDetail(err, fallback) {
  try { return JSON.parse(await err.response.data.text()).detail || fallback; } catch (e) { return err.response?.data?.detail || fallback; }
}

// "Cargo documents" tab: the export document set for one booking, generated from its bill of lading.
export function CargoDocumentsPanel({ bookingId, onOpenCarrier }) {
  const [data, setData] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('set');
  const [recording, setRecording] = useState(null);
  const [openVersions, setOpenVersions] = useState({});
  const [preview, setPreview] = useState(null);
  const loadPreview = useCallback(async function () {
    if (!preview) return null;
    try { return (await cargoApi.preview(bookingId, preview.key)).data; } catch (err) { throw await errorDetail(err, 'Could not render the document'); }
  }, [bookingId, preview]);

  const load = useCallback(async function () {
    if (!bookingId) { setData(null); setArchive([]); return; }
    const res = await Promise.all([cargoApi.workspace(bookingId), bookingsApi.documentArchive(bookingId)]);
    setData(res[0].data); setArchive(res[1].data.versions || []);
  }, [bookingId]);

  useEffect(function () {
    setLoading(true);
    load().catch(function () { toast.error('Could not load the cargo document workspace'); }).finally(function () { setLoading(false); });
  }, [load]);

  async function apply(promise, okMessage) {
    try {
      const res = await promise;
      setData(res.data);
      const arch = await bookingsApi.documentArchive(bookingId);
      setArchive(arch.data.versions || []);
      if (okMessage) toast.success(okMessage);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    }
  }

  async function laneChange(change) {
    setBusy('lane');
    await apply(cargoApi.saveProfile(bookingId, { ...change, data: {} }), 'Lane updated — document set recalculated');
    setBusy('');
  }

  async function saveProfile(values) {
    setBusy('profile');
    await apply(cargoApi.saveProfile(bookingId, { data: values }), 'Compliance profile saved');
    setBusy('');
  }

  async function pdf(r) {
    setBusy('pdf-' + r.key);
    try {
      const res = await cargoApi.downloadPdf(bookingId, r.key);
      saveBlob(res, r.title.replace(/[^A-Za-z]/g, '') + '.pdf', 'application/pdf');
      toast.success(r.title + ' generated');
      await load();
    } catch (err) { toast.error(await errorDetail(err, 'Could not generate the document')); } finally { setBusy(''); }
  }

  async function generateAll() {
    setBusy('all');
    try {
      const res = await cargoApi.generateAll(bookingId);
      saveBlob(res, 'CargoDocuments_' + data.booking_number + '.zip', 'application/zip');
      toast.success((res.headers['x-document-count'] || 'All') + ' documents generated from B/L ' + (data.bl_number || data.booking_number));
      await load();
    } catch (err) { toast.error(await errorDetail(err, 'Could not generate the document set')); } finally { setBusy(''); }
  }

  async function file(r) {
    setBusy('file-' + r.key);
    try {
      const res = await cargoApi.downloadFile(bookingId, r.key);
      saveBlob(res, r.external.filename, res.headers['content-type']);
    } catch (err) { toast.error('The file could not be retrieved'); } finally { setBusy(''); }
  }

  async function archived(v) {
    setBusy('arch-' + v.id);
    try {
      const res = await bookingsApi.downloadArchivedDocument(bookingId, v.id);
      saveBlob(res, v.filename, 'application/pdf');
    } catch (err) { toast.error('The archived copy could not be retrieved'); } finally { setBusy(''); }
  }

  function clear(r) {
    if (!window.confirm('Remove the recorded ' + r.title + '?')) return;
    apply(cargoApi.clearExternal(bookingId, r.key), r.title + ' cleared');
  }

  if (!bookingId) return <p className="text-gray-600 text-sm py-6" data-testid="cargo-empty">Select a booking to build its export document set.</p>;
  if (loading && !data) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!data) return null;
  const generatable = data.requirements.filter(function (r) { return r.generated; }).length;
  const seg = 'px-3 py-1.5 rounded-[3px] text-sm transition-colors duration-[120ms] ';

  return (
    <div className="space-y-6" data-testid="cargo-panel">
      <LaneHeader data={data} onLaneChange={laneChange} busy={busy === 'lane'} />
      <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
        <div className="inline-flex rounded-sm border border-gray-200 p-0.5 bg-gray-50">
          <button type="button" onClick={function () { setTab('set'); }} className={seg + (tab === 'set' ? 'bg-white text-ink shadow-sm' : 'text-gray-600 hover:text-ink')} data-testid="cargo-tab-set">Document set ({data.requirements.length})</button>
          <button type="button" onClick={function () { setTab('profile'); }} className={seg + (tab === 'profile' ? 'bg-white text-ink shadow-sm' : 'text-gray-600 hover:text-ink')} data-testid="cargo-tab-profile">Compliance profile ({data.fields.length} fields)</button>
        </div>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="rounded-sm" onClick={function () { load(); }} disabled={busy !== ''} data-testid="cargo-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
        <Button size="sm" className="rounded-sm bg-blue-500 text-ink hover:bg-blue-600" onClick={generateAll} disabled={busy !== '' || generatable === 0} data-testid="cargo-generate-all">
          {busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FolderDown className="h-4 w-4 mr-1.5" /> Generate all {generatable} from B/L</>}
        </Button>
      </div>
      {tab === 'set' ? (
        <div className="grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-6 items-start">
          <RequirementList requirements={data.requirements} archive={archive} busy={busy} openVersions={openVersions} toggleVersions={function (k) { setOpenVersions(function (p) { return { ...p, [k]: !p[k] }; }); }}
            onPdf={pdf} onPreview={setPreview} onRecord={setRecording} onFile={file} onClear={clear} onArchived={archived} onCarrier={onOpenCarrier} />
          <ReadinessPanel validation={data.validation} />
        </div>
      ) : (
        <ProfileForm data={data} onSave={saveProfile} saving={busy === 'profile'} />
      )}
      <ExternalDocDialog bookingId={bookingId} requirement={recording} onOpenChange={function (o) { if (!o) setRecording(null); }} onSaved={function (ws) { setData(ws); }} />
      <DocumentPreview open={Boolean(preview)} onOpenChange={function (o) { if (!o) setPreview(null); }} load={loadPreview} title={preview ? preview.title : ''} downloading={preview ? busy === 'pdf-' + preview.key : false}
        onDownload={preview ? async function () { await pdf(preview); setPreview(null); } : null} />
    </div>
  );
}
